import { type DocumentData, type DocumentSnapshot, doc } from 'firebase/firestore'

import { DEFAULT_LOCALE, isSupportedLocale } from '@/i18n'
import { callable } from '@/lib/firebase/callable'
import { createReasonGuard } from '@/lib/firebase/errors'
import { type Subscription, subscribeDoc } from '@/lib/firebase/subscribe'
import {
  type SetupScope,
  asIsoOrNull,
  asOneOf,
  asRecords,
  asString,
  asStrings,
  isRecord,
  setupCollection,
  subscribeLatestRun,
  toBrandDocMeta,
  toRunEntry,
  toSetupRun,
} from '@/repositories/setup-docs'
import {
  COMPANY_SIZE_BANDS,
  CUSTOMER_PROFILE_ERROR_CODES,
  CUSTOMER_PROFILE_REFUSAL_REASONS,
  CUSTOMER_PROFILE_RUN_STAGES,
  type CompanySizeBand,
  type CustomerProfile,
  type CustomerProfileRun,
  type JobStory,
  PROFILE_BASES,
  type ProfileIdentity,
  type ProfileJob,
  type ProfileRow,
  type ProfileSummary,
} from '@/types/customer-profile'

function asCompanySize(value: unknown): CompanySizeBand | null {
  return COMPANY_SIZE_BANDS.find((band) => band === value) ?? null
}

function fieldsOf(value: unknown): DocumentData {
  return isRecord(value) ? value : {}
}

function toRow(value: unknown): ProfileRow {
  const row = fieldsOf(value)
  return { items: asStrings(row.items), basis: asOneOf(row.basis, PROFILE_BASES, 'empty') }
}

function toIdentity(value: unknown): ProfileIdentity {
  const identity = fieldsOf(value)
  return {
    title: asString(identity.title),
    companySize: asCompanySize(identity.companySize),
    basis: asOneOf(identity.basis, PROFILE_BASES, 'empty'),
  }
}

function toStory(record: DocumentData): JobStory {
  return {
    when: asString(record.when),
    want: asString(record.want),
    soThat: asString(record.soThat),
  }
}

function toJob(value: unknown): ProfileJob {
  const job = fieldsOf(value)
  const ladder = fieldsOf(job.ladder)
  return {
    ladder: { goal: asString(ladder.goal), progress: asString(ladder.progress) },
    stories: asRecords(job.stories).map(toStory),
    basis: asOneOf(job.basis, PROFILE_BASES, 'empty'),
  }
}

function toSummary(value: unknown): ProfileSummary {
  const summary = fieldsOf(value)
  return { text: asString(summary.text), basis: asOneOf(summary.basis, PROFILE_BASES, 'empty') }
}

function toCustomerProfile(data: DocumentData): CustomerProfile {
  return {
    meta: toBrandDocMeta(data.meta),
    identity: toIdentity(data.identity),
    job: toJob(data.job),
    tried: toRow(data.tried),
    blockers: toRow(data.blockers),
    summary: toSummary(data.summary),
    language: isSupportedLocale(data.language) ? data.language : DEFAULT_LOCALE,
    runAt: asIsoOrNull(data.runAt),
  }
}

function toCustomerProfileRun(
  snapshot: DocumentSnapshot<DocumentData>,
): CustomerProfileRun | undefined {
  const data = snapshot.data()
  if (!data) return undefined
  const { errorCode, ...run } = toSetupRun(
    snapshot.id,
    data,
    CUSTOMER_PROFILE_RUN_STAGES,
    toRunEntry,
  )
  return {
    ...run,
    ...(errorCode !== undefined
      ? {
          errorCode: asOneOf(errorCode, CUSTOMER_PROFILE_ERROR_CODES, 'customer_profile_failed'),
        }
      : {}),
    keepEdited: data.keepEdited === true,
    startedByUserId: asString(data.startedByUserId),
  }
}

export function subscribeCustomerProfile(scope: SetupScope): Subscription<CustomerProfile> {
  return subscribeDoc(doc(setupCollection(scope, 'customerProfile'), 'current'), (snapshot) => {
    const data = snapshot.data()
    return data ? toCustomerProfile(data) : undefined
  })
}

export function subscribeLatestCustomerProfileRun(
  scope: SetupScope,
): Subscription<CustomerProfileRun | null> {
  return subscribeLatestRun(scope, 'customerProfileRuns', toCustomerProfileRun)
}

export interface StartCustomerProfileInput {
  keepEdited: boolean
}

export interface StartCustomerProfileResult {
  runId: string
}

export const isCustomerProfileRunInProgressReason = createReasonGuard([
  'customer_profile_run_in_progress',
] as const)

export const isCustomerProfileRefusalReason = createReasonGuard(CUSTOMER_PROFILE_REFUSAL_REASONS)

export const startCustomerProfile = callable<StartCustomerProfileInput, StartCustomerProfileResult>(
  'startCustomerProfile',
)

type EmptyInput = Record<string, never>

const writeCustomerProfileSummaryCallable = callable<EmptyInput, DocumentData>(
  'writeCustomerProfileSummary',
)

/** Writes a new summary from the current profile; not saved until the dialog saves it. */
export async function writeCustomerProfileSummary(): Promise<string> {
  const result = await writeCustomerProfileSummaryCallable({})
  return asString(result.text)
}

const confirmCustomerProfileCallable = callable<EmptyInput, DocumentData>('confirmCustomerProfile')

export async function confirmCustomerProfile(): Promise<void> {
  await confirmCustomerProfileCallable({})
}
